<!-- src/components/shop/SearchSuggestPanel.vue -->
<!--
  🔥 2026-10-15 헤더 검색창 드롭다운 (최근 검색어 / 인기 검색어 / 추천 카테고리)
  - body 로 띄워서(Teleport) 헤더 아래 메뉴줄에 가려지지 않는다. 위치는 검색창(anchor) 기준으로 계산.
  - PC: 최근 | 인기 두 칸 나란히, 모바일: 탭으로 전환하고 화면 폭에 맞춰 펼친다.
  - 클래스는 전부 nb-sp 접두사(다른 화면 스타일과 겹치지 않게).
-->
<template>
  <Teleport to="body">
    <div class="nb-sp" :class="{ 'nb-sp--mobile': mobile }" :style="panelStyle" @mousedown.stop>
      <!-- 모바일 탭 -->
      <div class="nb-sp-tabs" v-if="mobile && showRecent && showPopular">
        <button type="button" :class="{ on: tab === 'recent' }" @click="tab = 'recent'; tabTouched = true">
          <i class="bi bi-clock-history"></i> 최근 검색어
        </button>
        <button type="button" :class="{ on: tab === 'popular' }" @click="tab = 'popular'; tabTouched = true">
          <i class="bi bi-fire"></i> 인기 검색어
        </button>
      </div>

      <div class="nb-sp-body" v-if="showRecent || showPopular">
        <!-- 최근 검색어 -->
        <section class="nb-sp-sec" v-if="showRecent" v-show="!mobile || !showPopular || tab === 'recent'">
          <div class="nb-sp-title" v-if="!mobile">
            <span><i class="bi bi-clock-history"></i> 최근 검색어</span>
            <button type="button" class="nb-sp-link" v-if="recent.length" @click="$emit('clear')">전체삭제</button>
          </div>
          <div class="nb-sp-title nb-sp-title--sub" v-else-if="recent.length">
            <span></span>
            <button type="button" class="nb-sp-link" @click="$emit('clear')">전체삭제</button>
          </div>

          <ul class="nb-sp-recent" v-if="recent.length">
            <li v-for="r in recent" :key="r.keyword" @click="$emit('pick', r.keyword)">
              <i class="bi bi-search nb-sp-ic"></i>
              <span class="nb-sp-kw">{{ r.keyword }}</span>
              <span class="nb-sp-date" v-if="r.date">{{ r.date }}</span>
              <button type="button" class="nb-sp-x" title="삭제" @click.stop="$emit('remove', r.keyword)">
                <i class="bi bi-x-lg"></i>
              </button>
            </li>
          </ul>
          <div class="nb-sp-empty" v-else>
            <i class="bi" :class="saveOff ? 'bi-pause-circle' : 'bi-clock'"></i>
            <p v-if="saveOff">검색어 저장 기능이 꺼져 있어요</p>
            <p v-else>최근 검색한 내역이 없어요</p>
          </div>
        </section>

        <!-- 인기 검색어 -->
        <section class="nb-sp-sec nb-sp-sec--popular" v-if="showPopular" v-show="!mobile || !showRecent || tab === 'popular'">
          <div class="nb-sp-title">
            <span v-if="!mobile"><i class="bi bi-fire nb-sp-fire"></i> 인기 검색어</span>
            <span v-else></span>
            <small class="nb-sp-basis">최근 {{ popularDays }}일 · {{ basis }} 기준</small>
          </div>
          <ol class="nb-sp-rank" v-if="popular.length">
            <li v-for="p in popular" :key="p.keyword" @click="$emit('pick', p.keyword)">
              <span class="nb-sp-no" :class="{ top: p.rank <= 3 }">{{ p.rank }}</span>
              <span class="nb-sp-kw">{{ p.keyword }}</span>
            </li>
          </ol>
          <div class="nb-sp-empty" v-else>
            <i class="bi bi-bar-chart"></i>
            <p>아직 집계된 인기 검색어가 없어요</p>
            <small>고객들이 많이 찾는 검색어가 여기에 순위로 보여요</small>
          </div>
        </section>
      </div>

      <!-- 추천 카테고리 -->
      <div class="nb-sp-cats" v-if="showCategories && categories.length">
        <span class="nb-sp-cats-title"><i class="bi bi-grid"></i> 추천 카테고리</span>
        <div class="nb-sp-chips">
          <button type="button" class="nb-sp-chip" v-for="c in categories" :key="c.id"
                  @click="$emit('pick-category', c)"># {{ c.title }}</button>
        </div>
      </div>

      <div class="nb-sp-foot">
        <button type="button" class="nb-sp-link" v-if="showRecent" @click="$emit('toggle-save')">
          <i class="bi" :class="saveOff ? 'bi-toggle-off' : 'bi-toggle-on'"></i>
          검색어 저장 {{ saveOff ? '꺼짐' : '켜짐' }}
        </button>
        <span v-else></span>
        <button type="button" class="nb-sp-link" @click="$emit('close')">닫기</button>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: 'SearchSuggestPanel',
  props: {
    anchor: { type: Object, default: null },      // 검색창 DOM (위치 계산 기준)
    mobile: { type: Boolean, default: false },
    recent: { type: Array, default: () => [] },   // [{ keyword, date }]
    popular: { type: Array, default: () => [] },  // [{ rank, keyword, count }]
    categories: { type: Array, default: () => [] }, // [{ id, title }]
    saveOff: { type: Boolean, default: false },
    basis: { type: String, default: '' },
    // 2026-10-19 관리자 검색어 관리 설정 (기본값 = 예전과 같은 화면)
    showRecent: { type: Boolean, default: true },
    showPopular: { type: Boolean, default: true },
    showCategories: { type: Boolean, default: true },
    popularDays: { type: Number, default: 7 }
  },
  emits: ['pick', 'remove', 'clear', 'toggle-save', 'close', 'pick-category'],
  data() {
    return { tab: 'recent', rect: null, tabTouched: false };
  },
  watch: {
    // 2026-10-19 로그인 회원의 최근 검색어는 서버에서 늦게 도착한다 → 도착하면 (직접 탭을 누르지 않았을 때) 최근 탭으로
    recent(list) {
      if (this.mobile && !this.tabTouched && list && list.length) this.tab = 'recent';
    }
  },
  computed: {
    panelStyle() {
      const r = this.rect;
      if (!r) return { visibility: 'hidden' };
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const top = r.bottom + 6;
      if (this.mobile) {
        return { top: top + 'px', left: '8px', right: '8px', maxHeight: Math.max(240, vh - top - 12) + 'px' };
      }
      const width = Math.min(Math.max(r.width, 600), vw - 32);
      const left = Math.min(Math.max(16, r.left), vw - width - 16);
      return { top: top + 'px', left: left + 'px', width: width + 'px', maxHeight: Math.max(260, vh - top - 16) + 'px' };
    }
  },
  mounted() {
    // 최근 검색어가 없으면 모바일은 인기 탭부터
    if (this.mobile && !this.recent.length) this.tab = 'popular';
    this.measure();
    window.addEventListener('scroll', this.measure, { passive: true });
    window.addEventListener('resize', this.measure);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.measure);
    window.removeEventListener('resize', this.measure);
  },
  methods: {
    measure() {
      if (!this.anchor || !this.anchor.getBoundingClientRect) return;
      this.rect = this.anchor.getBoundingClientRect();
    }
  }
};
</script>

<style scoped>
.nb-sp {
  position: fixed;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 14px 40px rgba(26, 20, 18, 0.18), 0 2px 6px rgba(26, 20, 18, 0.06);
  border: 1px solid #f0e3e0;
  overflow: hidden;
  animation: nb-sp-in 0.16s ease-out;
  text-align: left;
  font-size: 14px;
  color: #2b2b2b;
}
@keyframes nb-sp-in {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.nb-sp-body {
  display: flex;
  min-height: 0;
  overflow-y: auto;
}
.nb-sp-sec {
  flex: 1;
  min-width: 0;
  padding: 16px 18px 12px;
}
.nb-sp-sec + .nb-sp-sec {
  border-left: 1px solid #f3eceb;
  background: linear-gradient(180deg, #fffaf9 0%, #ffffff 70%);
}
.nb-sp-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-weight: 700;
  font-size: 14px;
}
.nb-sp-title .bi { margin-right: 4px; color: #b12e19; }
.nb-sp-fire { color: #ff5a36 !important; }
.nb-sp-basis { font-weight: 400; font-size: 11px; color: #a39996; }
.nb-sp-title--sub { margin-bottom: 4px; }

.nb-sp-link {
  border: none;
  background: none;
  padding: 2px 4px;
  font-size: 12px;
  color: #8a817f;
  cursor: pointer;
}
.nb-sp-link:hover { color: #b12e19; }
.nb-sp-link .bi { font-size: 15px; vertical-align: -2px; margin-right: 2px; }

/* 최근 검색어 */
.nb-sp-recent { list-style: none; margin: 0; padding: 0; }
.nb-sp-recent li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
}
.nb-sp-recent li:hover { background: #fdf1ee; }
.nb-sp-ic { color: #c9bfbd; font-size: 13px; }
.nb-sp-kw {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nb-sp-date { font-size: 11px; color: #b5aba9; }
.nb-sp-x {
  border: none;
  background: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  color: #b5aba9;
  font-size: 11px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.nb-sp-x:hover { background: #f1e4e1; color: #b12e19; }

/* 인기 검색어 — PC 는 5개씩 두 줄 */
.nb-sp-rank {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-flow: column;
  grid-template-rows: repeat(5, auto);
  column-gap: 14px;
}
.nb-sp-rank li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 6px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;
}
.nb-sp-rank li:hover { background: #fdf1ee; }
.nb-sp-rank li:hover .nb-sp-kw { color: #b12e19; text-decoration: underline; }
.nb-sp-no {
  flex: 0 0 22px;
  height: 22px;
  border-radius: 6px;
  background: #f2eeed;
  color: #7d7472;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.nb-sp-no.top { background: linear-gradient(135deg, #e0452b, #b12e19); color: #fff; }

.nb-sp-empty {
  text-align: center;
  padding: 22px 8px 18px;
  color: #a39996;
}
.nb-sp-empty .bi { font-size: 26px; color: #e2d6d3; }
.nb-sp-empty p { margin: 8px 0 2px; font-size: 13px; color: #8a817f; }
.nb-sp-empty small { font-size: 11px; }

/* 추천 카테고리 */
.nb-sp-cats {
  padding: 12px 18px 4px;
  border-top: 1px solid #f3eceb;
}
.nb-sp-cats-title { font-size: 12px; font-weight: 700; color: #6f6765; }
.nb-sp-cats-title .bi { color: #b12e19; margin-right: 3px; }
.nb-sp-chips { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 0 6px; }
.nb-sp-chip {
  border: 1px solid #ecdcd8;
  background: #fff;
  color: #5a5250;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.12s;
}
.nb-sp-chip:hover { background: #b12e19; border-color: #b12e19; color: #fff; }

.nb-sp-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: #faf7f6;
  border-top: 1px solid #f3eceb;
}

/* 모바일 */
.nb-sp-tabs {
  display: flex;
  border-bottom: 1px solid #f0e6e4;
}
.nb-sp-tabs button {
  flex: 1;
  border: none;
  background: #fff;
  padding: 12px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #9b9290;
  border-bottom: 2px solid transparent;
  cursor: pointer;
}
.nb-sp-tabs button.on { color: #b12e19; border-bottom-color: #b12e19; }
.nb-sp--mobile { border-radius: 12px; }
.nb-sp--mobile .nb-sp-body { display: block; }
.nb-sp--mobile .nb-sp-sec { padding: 12px 14px 8px; }
.nb-sp--mobile .nb-sp-sec + .nb-sp-sec { border-left: none; background: #fff; }
.nb-sp--mobile .nb-sp-rank { grid-template-columns: 1fr; grid-auto-flow: row; grid-template-rows: none; }
.nb-sp--mobile .nb-sp-recent li,
.nb-sp--mobile .nb-sp-rank li { padding: 10px 6px; }
.nb-sp--mobile .nb-sp-cats { padding: 10px 14px 2px; }
</style>
